import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Loader2 } from "lucide-react";
import { useI18n } from "@/i18n/I18nContext";

/**
 * Landing pad for all Supabase OAuth redirects.
 *
 * Supabase appends ?code=... (PKCE) or #access_token=... to the redirectTo URL.
 * detectSessionInUrl=true on the client automatically exchanges the code for a
 * session and fires onAuthStateChange(SIGNED_IN).  This page just waits for
 * that event on an unprotected route so no auth guard fires prematurely.
 *
 * On success → /dashboard
 * On timeout (10 s) → /login with a descriptive error in location state
 */
async function completeNuvraSession(session: { access_token: string }): Promise<boolean> {
  try {
    const response = await fetch("/api/auth/email-verification/complete", {
      method: "POST",
      credentials: "include",
      headers: { Authorization: `Bearer ${session.access_token}` },
    });
    return response.ok;
  } catch {
    return false;
  }
}

export default function AuthCallback() {
  const navigate = useNavigate();
  const { t } = useI18n();
  const done = useRef(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (!done.current) {
        navigate("/login", {
          replace: true,
          state: { error: "Sign-in timed out. Please try again." },
        });
      }
    }, 10_000);

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (done.current) return;
      if ((event === "SIGNED_IN" || event === "INITIAL_SESSION") && session) {
        done.current = true;
        clearTimeout(timer);
        void completeNuvraSession(session).then((ok) => {
          if (ok) navigate("/dashboard", { replace: true });
          else {
            void supabase.auth.signOut();
            navigate("/login", { replace: true, state: { error: "Email verified, but we could not finish your Nuvra session. Please sign in again." } });
          }
        });
      }
    });

    // If session is already present (e.g. page reload on this path)
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (done.current) return;
      if (session) {
        done.current = true;
        clearTimeout(timer);
        void completeNuvraSession(session).then((ok) => {
          if (ok) navigate("/dashboard", { replace: true });
          else {
            void supabase.auth.signOut();
            navigate("/login", { replace: true, state: { error: "Email verified, but we could not finish your Nuvra session. Please sign in again." } });
          }
        });
      }
    });

    return () => {
      subscription.unsubscribe();
      clearTimeout(timer);
    };
  }, [navigate]);

  return (
    <div className="flex items-center justify-center h-screen bg-background">
      <div className="text-center space-y-4">
        <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto" />
        <p className="text-sm text-muted-foreground">{t("auth.completingSignIn")}</p>
      </div>
    </div>
  );
}
